/**
 * MissingSizePrompt (Phase 86, UI-SPEC S3, D-10): sizes a product has that
 * the Platen Size rules for its type do not cover get no file slot. This
 * block lists them and lets an admin add each one to the org's rules without
 * leaving the product.
 *
 * Prop-driven and self-contained: the parent (`DesignsTier`) passes the
 * uncovered sizes from the one page-level platen read (86-12's
 * `platenGroupsForProduct(...).uncovered`) and that type's rule table. The
 * add is ONE atomic server statement (`platenSizeRules.addSize`, 86-07), so
 * there is no conflict or retry path. On success both platen reads are
 * invalidated and the size lands in its platen variant group on the next
 * render; nothing here changes the product's React key, so the expanded row
 * and any staged files (held above this component) survive.
 *
 * Preselection never guesses: the platen is preselected only when every ruled
 * size of this type uses it, and the platen variant only when they also all
 * share one. Otherwise the pair starts empty and the button stays disabled
 * until both are chosen, so a size can never be routed silently.
 *
 * `admin` only hides the controls; the server enforces the role.
 *
 * BLU-1238: a kept product (an active skip platen mark) says its sizes go on
 * printing its current files, never that they wait. When the server refuses
 * the add because the org's existing products are not kept yet (86-29's save
 * guard, PRECONDITION_FAILED), the block says so in plain words instead of
 * the generic error toast.
 *
 * Accessibility: the block is a labelled `section`, not a live region (a live
 * region around the Selects would announce every pick). One separate sr-only
 * status line says which size was just added; it outlives the block, which
 * unmounts once its last size is added.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { buildSizeRuns, type PlatenSizeRuleTable } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import {
  PlatenAssignmentSelects,
  type PlatenAssignmentDraft,
} from '@/components/shared/PlatenAssignmentSelects';
import { formatPlatenSizeRange } from '@/lib/platen-size-range';
import { trpc } from '@/lib/trpc';

export interface MissingSizePromptProps {
  /** The store's product type name, for the copy. Falls back to the code. */
  productTypeName: string | null | undefined;
  /** The org product type code the rules are keyed by (for example `TEE`). */
  productTypeCode: string;
  /** Size codes on this product with no rule and no override. */
  sizes: readonly string[];
  /** This product type's rule table (size code to assignment). */
  rulesForType: PlatenSizeRuleTable | null | undefined;
  admin: boolean;
  /** BLU-1238 (R3-2): the product keeps printing its current files. Default false. */
  keepsCurrentFiles?: boolean;
}

const EMPTY_DRAFT: PlatenAssignmentDraft = { platen: null, platenVariant: null };

/** The unambiguous starting pair for a new size of this type, or empty sides. */
function preselection(rulesForType: PlatenSizeRuleTable | null | undefined): PlatenAssignmentDraft {
  const assignments = Object.values(rulesForType ?? {});
  if (assignments.length === 0) return EMPTY_DRAFT;
  const platen = assignments[0]!.platen;
  if (!assignments.every((a) => a.platen === platen)) return EMPTY_DRAFT;
  const variant = assignments[0]!.platenVariant;
  return {
    platen,
    platenVariant: assignments.every((a) => a.platenVariant === variant) ? variant : null,
  };
}

const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/** 86-29's rule save guard: the org's existing products are not kept on their current files yet. */
const isNotReadyRefusal = (error: unknown): boolean =>
  (error as { data?: { code?: unknown } } | null)?.data?.code === 'PRECONDITION_FAILED';

export function MissingSizePrompt({
  productTypeName,
  productTypeCode,
  sizes,
  rulesForType,
  admin,
  keepsCurrentFiles = false,
}: MissingSizePromptProps): JSX.Element | null {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();
  const addSize = trpc.platenSizeRules.addSize.useMutation();

  const productType = productTypeName?.trim() || productTypeCode;
  const initial = useMemo(() => preselection(rulesForType), [rulesForType]);

  // Per-size picks the operator made. A size without an entry shows `initial`.
  const [picks, setPicks] = useState<Record<string, PlatenAssignmentDraft>>({});
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  // Sizes added in this session, hidden at once instead of waiting for the
  // refetch. Pruned when the parent stops listing them, so a size removed
  // from the rules later (in Settings) shows again.
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());
  // What the sr-only status line says ("XL added to the TEE rules").
  const [announcement, setAnnouncement] = useState('');
  // The save guard's refusal, shown in the block until an add succeeds.
  const [refused, setRefused] = useState(false);
  const titleId = useId();

  useEffect(() => {
    setAdded((current) => {
      const next = new Set([...current].filter((size) => sizes.includes(size)));
      return next.size === current.size ? current : next;
    });
  }, [sizes]);

  // The shared size ladder (the same order the platen groups use).
  const rows = useMemo(
    () => buildSizeRuns(sizes).flat().filter((size) => !added.has(size)),
    [sizes, added],
  );

  const status = (
    <p role="status" className="sr-only">
      {announcement}
    </p>
  );

  if (rows.length === 0) return announcement ? status : null;

  const draftFor = (size: string): PlatenAssignmentDraft => picks[size] ?? initial;

  const setFlag = (setter: typeof setPending, size: string, on: boolean) =>
    setter((current) => {
      const next = new Set(current);
      if (on) next.add(size);
      else next.delete(size);
      return next;
    });

  const handleAdd = async (size: string): Promise<void> => {
    const draft = draftFor(size);
    if (!draft.platen || !draft.platenVariant || pending.has(size)) return;
    setFlag(setPending, size, true);
    try {
      await addSize.mutateAsync({
        productTypeCode,
        sizeCode: size,
        platen: draft.platen,
        platenVariant: draft.platenVariant,
      });
      toast.dismiss(`platen-missing-size-error:${size}`);
      setRefused(false);
      const message = t('platen.missingSize.success', { size, productType });
      toast.success(message);
      setAnnouncement(message);
      setFlag(setAdded, size, true);
      void utils.productSync.getPlatenSizeContext.invalidate();
      void utils.platenSizeRules.getRules.invalidate();
    } catch (error) {
      if (isNotReadyRefusal(error)) {
        // Not a fault to retry: the plain refusal, in the block.
        setRefused(true);
        return;
      }
      // The row stays with its picks; the error persists until dismissed.
      toast.error(t('platen.missingSize.error', { size, message: errorMessage(error) }), {
        id: `platen-missing-size-error:${size}`,
        duration: Infinity,
      });
    } finally {
      setFlag(setPending, size, false);
    }
  };

  return (
    <section aria-labelledby={titleId} className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
      {status}
      <div className="space-y-1">
        <p id={titleId} className="text-sm font-semibold text-amber-900">
          {rows.length === 1
            ? t('platen.missingSize.title_one', { size: rows[0] })
            : t('platen.missingSize.title_other', { count: rows.length })}
        </p>
        <p className="text-[11px] leading-4 text-amber-800">
          {admin
            ? t('platen.missingSize.explainer', { productType })
            : t('platen.missingSize.nonAdmin', {
                sizes: formatPlatenSizeRange(t, rows),
                productType,
              })}
        </p>
        {keepsCurrentFiles && (
          <p className="text-[11px] leading-4 text-amber-800">{t('platen.missingSize.keptNote')}</p>
        )}
      </div>

      {refused && (
        <p role="alert" className="rounded-md border border-amber-400 bg-background px-2.5 py-2 text-xs text-amber-900">
          {t('platen.missingSize.refusedNotReady')}
        </p>
      )}

      {admin && (
        <ul className="space-y-2">
          {rows.map((size) => {
            const draft = draftFor(size);
            const busy = pending.has(size);
            const complete = draft.platen !== null && draft.platenVariant !== null;
            return (
              <li key={size} className="flex flex-wrap items-center gap-2">
                <span className="min-w-12 font-mono text-xs font-semibold text-amber-900">{size}</span>
                <PlatenAssignmentSelects
                  value={draft}
                  onChange={(next) => setPicks((current) => ({ ...current, [size]: next }))}
                  sizeLabel={size}
                  productTypeLabel={productTypeCode}
                  size="compact"
                  disabled={busy}
                />
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1.5 bg-threadheads-purple text-xs text-white hover:bg-threadheads-purple/90"
                  disabled={!complete || busy}
                  aria-busy={busy || undefined}
                  onClick={() => void handleAdd(size)}
                >
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden />}
                  {t('platen.missingSize.add', { size })}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
